<script setup>
import axios from "axios";
import dayjs from "dayjs";

// Composables and reactive data
import categoriesData from "~/utils/data/categories-data.json";
const config = useRuntimeConfig().public;
const route = useRoute();
const $categories = categoriesData;

// Reactive data (replacing data() properties)
const posts = ref([]);
const totalPages = ref(0);
const currentPage = ref(1);
const showLoad = ref(false);
const loadMore = ref(false);
const loadMoreAnchor = ref(null);

// URL mappings (extracted to avoid duplication)
const urlMappings = [
	{
		url: "/blog/",
		name: "Blog",
		caption: "This is our main blog.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down complex concepts for the everyday trader."
	},
	{
		url: "/uncategorized/",
		name: "Blog",
		caption: "This is our main blog.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down complex concepts for the everyday trader."
	},
	{
		url: "/trading/",
		name: "Trading",
		caption: "This is where we discuss stock trading.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down complex concepts for the everyday trader."
	},
	{
		url: "/trader-tips/",
		name: "Trader Tips",
		caption: "This is where we discuss trading strategy.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down trading strategy for the everyday trader."
	},
	{
		url: "/trading-news/",
		name: "Trading News",
		caption: "This is where we discuss trading news.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down top trading news for the everyday trader."
	},
	{
		url: "/crypto/",
		name: "Crypto",
		caption: "This is where we discuss crypto.",
		meta:
			"StocksToTrade brings top trading knowledge and insights to its explainer articles, breaking down cryptocurrency concepts for the everyday trader."
	},
	{
		url: "/bohens-take/",
		name: "Bohen's Take",
		caption: "This is where I give my two cents on the market!",
		meta:
			"Here's where StocksToTrade lead trainer Tim Bohen shares his top trading knowledge and insights for the everyday trader — straight from the horse's mouth!"
	},
	{
		url: "/announcements/",
		name: "Announcements",
		caption: "This is our announcement board.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles — get our most important announcements by checking here!"
	},
	{
		url: "/cryptocurrency/",
		name: "Crypto Currency",
		caption: "This is where we discuss crypto.",
		meta:
			"StocksToTrade brings top trading knowledge and insights to its explainer articles, breaking down complex concepts like crypto for the everyday trader."
	},
	{
		url: "/trading-plan/",
		name: "Trading Plan",
		caption: "This is where we break down trading plans.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down trading plans for the everyday trader."
	},
	{
		url: "/chart-of-the-day/",
		name: "Chart of the Day",
		caption: "This is where we break down stock charts.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down stock charts for the everyday trader."
	},
	{
		url: "/infographic/",
		name: "Infographic",
		caption: "These are our easy-to-understand infographics.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer infographics, breaking down complex concepts for the everyday trader."
	},
	{
		url: "/featured/",
		name: "Featured",
		caption: "This is the home for our most important articles.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer features, breaking down complex concepts for the everyday trader."
	},
	{
		url: "/podcast/",
		name: "Podcast",
		caption: "Welcome to podcast central.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer podcasts, breaking down complex concepts for the everyday trader."
	},
	{
		url: "/news/",
		name: "News",
		caption: "This is where we discuss trading news.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down trading news for the everyday trader."
	},
	{
		url: "/pro-webinars/",
		name: "Pro Webinars",
		caption: "This is some of the behind-the-scenes work from our webinars.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its member-only services — here's a FREE glimpse of the webinars we deliver!"
	},
	{
		url: "/discipline/",
		name: "Discipline",
		caption: "Discipline is the bedrock for becoming a self-sufficient trader.",
		meta:
			"StocksToTrade brings top trading knowledge and insights to its articles — here we discuss why discipline is important for becoming a self-sufficient trader."
	},
	{
		url: "/stock-exchanges/",
		name: "Stock Exchanges",
		caption: "Here's some general stock market news...",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down stock market news for the everyday trader."
	},
	{
		url: "/pro-articles/",
		name: "Pro Articles",
		caption: "Here's where we give a glimpse of our member-only trading intel.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its member-only services — here's a FREE glimpse of the kind of intel we deliver!"
	},
	{
		url: "/weekly-review/",
		name: "Weekly Review",
		caption: "Catch up on all the trading developments of the week.",
		meta:
			"StocksToTrade brings top trading knowledge and insights to its explainer articles, breaking down complex trading developments for the everyday trader."
	},
	{
		url: "/blog/?search=stocks%20to%20watch",
		name: "Top Stocks to Watch",
		caption: "These are our top stocks to watch!",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its stock watchlists, not only telling you which stocks to look at but also WHY."
	},
	{
		url: "/stock-analysis/",
		name: "Stock Analysis",
		caption: "This is where we break down stock analysis concepts.",
		meta:
			"StocksToTrade brings top trading knowledge and insights to its explainer articles, breaking down stock analysis concepts for the everyday trader."
	},
	{
		url: "/nfts/",
		name: "NFTs",
		caption: "This is where we discuss NFTs.",
		meta:
			"StocksToTrade brings top trading knowledge and actionable insights to its explainer articles, breaking down complex concepts like NFTs for the everyday trader."
	}
];

// Computed properties
const currentPath = computed(() => route.params.id?.[0] || "");

const categoryMeta = computed(() => {
	const matchedMapping = urlMappings.find(
		(mapping) => mapping.url === `/${currentPath.value}/`
	);
	return (
		matchedMapping || { name: "Loading...", caption: "", slug: "", meta: "" }
	);
});

const category_id = computed(() => {
	if (!$categories || $categories.length === 0 || !route.params.id) {
		return null;
	}
	const category = $categories.filter(
		(category) => category.slug === route.params.id.join("")
	);

	if (category.length === 0) {
		return null;
	}

	return category[0].id;
});

const category_slug = computed(() => {
	if (!route.params.id || !route.params.id.length) {
		return { name: "Loading...", caption: "", slug: "", meta: "" };
	}

	const currentRoutePath = route.path;

	const matchedMapping = urlMappings.find((mapping) =>
		currentRoutePath.includes(mapping.url)
	);

	if (matchedMapping) {
		return {
			name: matchedMapping.name,
			caption: matchedMapping.caption,
			slug: matchedMapping.url,
			meta: matchedMapping.meta
		};
	}

	const category =
		!$categories || $categories.length === 0 || !route.params.id
			? null
			: $categories.find((category) => category.slug === route.params.id.join(""));

	if (category) {
		return {
			name: category.name,
			caption: category.caption,
			slug: category.slug,
			meta: category.meta
		};
	}

	return { name: "Loading...", caption: "" };
});

// Methods (converted to regular functions)
const filterCategory = (id) => {
	if (!$categories || $categories.length === 0) {
		return null;
	}

	const category = $categories.find((category) => category.id === id);
	return category;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatLink = (url) => {
	const lowercaseString = url.toLowerCase();
	return "/" + lowercaseString + "/";
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const truncateText = (text, maxLength) => {
	if (text.length <= maxLength) {
		return text;
	} else {
		return text.substring(0, maxLength) + "...";
	}
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const dateFormat = (date) => {
	return dayjs(date).format("MMMM D, YYYY");
};

const fetchMedia = async (data) => {
	let new_arr = data.map(async (item) => {
		const arr = item;

		if (item.featured_media) {
			const { data } = await axios.get(
				config.hostname + "wp-json/wp/v2/media/" + item.featured_media
			);

			arr.featured_media = data;
		}
		return { ...arr };
	});

	new_arr = await Promise.all([...new_arr]);
	return new_arr;
};

const fetchMorePosts = async () => {
	let post_list = [];
	currentPage.value += 1;

	if (route.query && route.query.search) {
		const { data } = await axios.get(
			config.hostname +
				"wp-json/wp/v2/posts?search=" +
				route.query.search +
				"&page=" +
				currentPage.value
		);

		const new_arr = await fetchMedia(data);
		post_list = new_arr;
	} else {
		const { data } = await axios.get(
			config.hostname +
				"wp-json/wp/v2/posts?category=" +
				category_id.value +
				"&page=" +
				currentPage.value +
				"&_embed"
		);

		post_list = data;
	}

	if (post_list.length) {
		posts.value = [...posts.value, ...post_list];
		loadMore.value = true;
		showLoad.value = false;
	}
};

const isElementInViewport = (element) => {
	if (!element) {
		return;
	}
	const rect = element.getBoundingClientRect();
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <=
			(window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	);
};

const handleVisibilityChange = () => {
	const visibilityCheck = isElementInViewport(loadMoreAnchor.value);

	if (
		visibilityCheck &&
		loadMore.value === true &&
		currentPage.value !== totalPages.value
	) {
		showLoad.value = true;
		loadMore.value = false;
		fetchMorePosts();
	}
};

// SSR data fetching (replacing asyncData)
const { data: pageData } = await useFetch(async () => {
	try {
		if (!route.params.id || route.params.id.length > 2) {
			throw createError({
				statusCode: 404,
				fatal: true
			});
		}

		// Fetch categories
		const response = await axios.get(
			config.hostname + "wp-json/wp/v2/categories?per_page=99"
		);

		const page_response = await axios.get(
			config.hostname + "wp-json/wp/v2/pages/3164"
		);

		if (page_response.data) {
			return {
				page: page_response.data,
				categories: response.data
			};
		}
	} catch (error) {
		throw createError({
			statusCode: 404,
			fatal: true,
			message: "Category not found"
		});
	}
});

// Extract page data
const page = computed(() => pageData.value?.page);

// Lifecycle hook (replacing mounted)
onMounted(async () => {
	if (import.meta.client) {
		window.addEventListener("scroll", handleVisibilityChange);

		let data = null;
		let headers = null;

		if (route.query && route.query.search) {
			const response = await axios.get(
				config.hostname + "wp-json/wp/v2/posts?search=" + route.query.search
			);

			if (response.data) {
				const new_arr = await fetchMedia(response.data);
				data = new_arr;
				headers = response.headers;
			}
		} else {
			if (!$categories || !route.params.id) {
				return;
			}

			const category = $categories.find(
				(category) => category.slug === route.params.id.join("")
			);

			if (!category) {
				return;
			}

			const categoryId = category.id;

			const response = await axios.get(
				config.hostname + `wp-json/wp/v2/posts?categories=${categoryId}&_embed`
			);

			if (response.data) {
				data = response.data;
				headers = response.headers;
			}
		}

		posts.value = data;
		totalPages.value = headers["x-wp-totalpages"];
		currentPage.value = 1;
		loadMore.value = true;
	}
});

// Cleanup on unmount
onUnmounted(() => {
	if (import.meta.client) {
		window.removeEventListener("scroll", handleVisibilityChange);
	}
});
</script>

<template>
	<Head>
		<Title>{{ categoryMeta.name }} - StocksToTrade</Title>
		<Meta
			name="description"
			:content="categoryMeta.meta"
		/>
		<Meta
			name="og:description"
			:content="categoryMeta.meta"
		/>
	</Head>
	<Head v-if="page && page.yoast_meta">
		<Link
			:href="config.current_hostname.slice(0, -1) + route.fullPath"
			rel="canonical"
		/>
		<Meta
			:content="config.current_hostname.slice(0, -1) + route.fullPath"
			property="og:url"
		/>

		<template
			v-for="(tag, i) in page.yoast_meta"
			:key="i"
		>
		<template
			v-if="
				tag.content &&
				!tag.content.startsWith('[vc_row') &&
				tag.name !== 'canonical' &&
				tag.name !== 'robots'
			"
		>
				<Meta
					:property="tag.property"
					:content="tag.content"
					v-if="tag.property && tag.property !== 'og:url'"
				/>
				<Meta
					:name="tag.name"
					:content="tag.content"
					v-else-if="tag.name"
				/>
			</template>
		</template>
	</Head>

	<Row class="!py-0">
		<Container>
			<hr class="border-slate-700" />
		</Container>
	</Row>
	<Row tag="section">
		<Container class="text-white">
			<Heading
				type="primary"
				:text="category_slug.name"
				tag="h1"
				class="mb-[12px]"
			/>

			<h2 class="text-base text-slate-300 mb-[64px]">
				{{ category_slug.caption }}
			</h2>

			<div class="blog-articles">
				<div
					class="col-12 mb-5"
					v-if="route.query && route.query.search"
				>
					<h2>Search results for "{{ route.query.search }}"</h2>
				</div>

				<BlogCard
					:key="i"
					v-bind="post"
					:category="filterCategory(post.categories[0])"
					v-for="(post, i) in posts"
				/>
			</div>
			<div
				v-if="posts.length"
				ref="loadMoreAnchor"
				class="post-loading mx-auto"
			>
				<img
					v-if="showLoad"
					src="/images/loading.gif"
					class="loading-gif mx-auto mt-[32px]"
				/>
			</div>
		</Container>
	</Row>
</template>
